/**
 * useInputTypeDetection Hook
 * 
 * Detecta automáticamente el tipo de input (email/phone/username)
 * y proporciona formateo y validación.
 * 
 * @module login/hooks/useInputTypeDetection
 */

import { useState, useCallback, useMemo } from 'react';

// ============================================================================
// Types
// ============================================================================

/** Tipos de input detectables */
export type DetectedInputType = 'email' | 'phone' | 'username' | null;

/** Resultado de la detección */
export interface DetectionResult {
    /** Tipo detectado */
    detectedType: DetectedInputType;
    /** Valor formateado (teléfonos con +549) */
    formattedValue: string;
    /** Mensaje de error si hay */
    errorMessage: string | null;
    /** Si la validación pasó */
    isValid: boolean;
}

/** Estado del hook */
export interface InputDetectionState extends DetectionResult {
    /** Valor raw del input */
    rawValue: string;
}

// ============================================================================
// Regex Patterns
// ============================================================================

/** Regex para validar email */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Regex para detectar patrón de teléfono (solo dígitos, espacios, +, -) */
const PHONE_PATTERN_REGEX = /^[+]?[\d\s-]+$/;

/** Regex para detectar si contiene al menos una letra */
const HAS_LETTER_REGEX = /[a-zA-Z]/;

/** Prefijo telefónico de Argentina */
const ARGENTINA_PREFIX = '+549';

// ============================================================================
// Detection Functions
// ============================================================================

/**
 * Detecta si el valor es un email válido
 */
export function isEmail(value: string): boolean {
    return EMAIL_REGEX.test(value);
}

/**
 * Detecta si el valor tiene patrón de teléfono
 * (solo dígitos, espacios, guiones, y opcionalmente + al inicio)
 */
export function isPhonePattern(value: string): boolean {
    // Debe tener al menos 1 dígito y solo caracteres permitidos
    const hasDigit = /\d/.test(value);
    const onlyPhoneChars = PHONE_PATTERN_REGEX.test(value);
    return hasDigit && onlyPhoneChars;
}

/**
 * Detecta si el valor contiene al menos una letra (username)
 */
export function hasLetter(value: string): boolean {
    return HAS_LETTER_REGEX.test(value);
}

/**
 * Formatea un número de teléfono, agregando +549 si es local Argentina
 */
export function formatPhoneNumber(value: string): string {
    // Limpiar espacios y guiones
    const cleaned = value.replace(/[\s-]/g, '');

    // Si ya tiene +, retornar limpio
    if (cleaned.startsWith('+')) {
        return cleaned;
    }

    // Agregar prefijo Argentina
    return `${ARGENTINA_PREFIX}${cleaned}`;
}

/**
 * Extrae solo los dígitos de un string
 */
export function extractDigits(value: string): string {
    return value.replace(/\D/g, '');
}

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Valida un email
 */
export function validateEmail(value: string): { isValid: boolean; error: string | null } {
    if (!EMAIL_REGEX.test(value)) {
        return { isValid: false, error: 'Ingresa un email válido' };
    }
    return { isValid: true, error: null };
}

/**
 * Valida un número de teléfono (8-15 dígitos)
 */
export function validatePhone(formattedValue: string): { isValid: boolean; error: string | null } {
    const digits = extractDigits(formattedValue);

    if (digits.length < 8) {
        return { isValid: false, error: 'El número de teléfono parece incompleto' };
    }

    if (digits.length > 15) {
        return { isValid: false, error: 'El número de teléfono es demasiado largo' };
    }

    return { isValid: true, error: null };
}

/**
 * Valida un nombre de usuario (mínimo 3 caracteres)
 */
export function validateUsername(value: string): { isValid: boolean; error: string | null } {
    if (value.length < 3) {
        return { isValid: false, error: 'El usuario debe tener al menos 3 caracteres' };
    }
    return { isValid: true, error: null };
}

// ============================================================================
// Main Detection Function
// ============================================================================

/**
 * Detecta el tipo de input y valida el valor
 * 
 * Orden de detección (crítico):
 * 1. Email: contiene @ y estructura válida
 * 2. Phone: solo dígitos, espacios, +, -
 * 3. Username: contiene al menos 1 letra
 * 4. Ninguno: error
 */
export function detectInputType(value: string): DetectionResult {
    const trimmed = value.trim();

    // Valor vacío
    if (!trimmed) {
        return {
            detectedType: null,
            formattedValue: '',
            errorMessage: null,
            isValid: false,
        };
    }

    // 1. Intentar detectar EMAIL (contiene @)
    if (trimmed.includes('@')) {
        const validation = validateEmail(trimmed);
        return {
            detectedType: 'email',
            formattedValue: trimmed.toLowerCase(),
            errorMessage: validation.error,
            isValid: validation.isValid,
        };
    }

    // 2. Intentar detectar PHONE (solo caracteres de teléfono)
    if (isPhonePattern(trimmed)) {
        const formatted = formatPhoneNumber(trimmed);
        const validation = validatePhone(formatted);
        return {
            detectedType: 'phone',
            formattedValue: formatted,
            errorMessage: validation.error,
            isValid: validation.isValid,
        };
    }

    // 3. Intentar detectar USERNAME (contiene al menos 1 letra)
    if (hasLetter(trimmed)) {
        const validation = validateUsername(trimmed);
        return {
            detectedType: 'username',
            formattedValue: trimmed,
            errorMessage: validation.error,
            isValid: validation.isValid,
        };
    }

    // 4. Ninguno: formato no reconocido
    return {
        detectedType: null,
        formattedValue: trimmed,
        errorMessage: 'Por favor ingresa un email, teléfono o usuario válido',
        isValid: false,
    };
}

// ============================================================================
// Hook
// ============================================================================

export interface UseInputTypeDetectionOptions {
    /** Si debe detectar en cada cambio o solo en blur */
    detectOnChange?: boolean;
    /** Valor inicial */
    initialValue?: string;
}

export interface UseInputTypeDetectionReturn extends InputDetectionState {
    /** Actualiza el valor y opcionalmente dispara detección */
    setValue: (value: string) => void;
    /** Dispara detección manualmente (para onBlur) */
    detect: () => void;
    /** Limpia el estado */
    reset: () => void;
}

/**
 * Hook para detección automática de tipo de input
 * 
 * @example
 * ```tsx
 * const { rawValue, detectedType, formattedValue, errorMessage, isValid, setValue, detect } = 
 *   useInputTypeDetection({ detectOnChange: false });
 * 
 * <Input 
 *   value={rawValue} 
 *   onChange={e => setValue(e.target.value)}
 *   onBlur={detect}
 * />
 * ```
 */
export function useInputTypeDetection(
    options: UseInputTypeDetectionOptions = {}
): UseInputTypeDetectionReturn {
    const { detectOnChange = false, initialValue = '' } = options;

    const [rawValue, setRawValue] = useState(initialValue);
    const [detectionResult, setDetectionResult] = useState<DetectionResult>(() =>
        detectInputType(initialValue)
    );

    /**
     * Actualiza el valor y opcionalmente dispara detección
     */
    const setValue = useCallback((value: string) => {
        setRawValue(value);

        if (detectOnChange) {
            setDetectionResult(detectInputType(value));
        } else {
            // Limpiar error mientras escribe (UX más amigable)
            setDetectionResult(prev => ({
                ...prev,
                errorMessage: null,
            }));
        }
    }, [detectOnChange]);

    /**
     * Dispara detección manualmente
     */
    const detect = useCallback(() => {
        setDetectionResult(detectInputType(rawValue));
    }, [rawValue]);

    /**
     * Limpia el estado
     */
    const reset = useCallback(() => {
        setRawValue('');
        setDetectionResult({
            detectedType: null,
            formattedValue: '',
            errorMessage: null,
            isValid: false,
        });
    }, []);

    return useMemo(() => ({
        rawValue,
        ...detectionResult,
        setValue,
        detect,
        reset,
    }), [rawValue, detectionResult, setValue, detect, reset]);
}

export default useInputTypeDetection;
